import { Api } from "../../../../services/interceptor";
import { QUERY_KEY } from "../../../../services/queryEndPoint";

export const PLAYER_INFO_FIELDS = [
  { value: "FULL_NAME", label: "Full Name" },
  { value: "DATE_OF_BIRTH", label: "Date of Birth" },
  { value: "EMAIL", label: "Email" },
  { value: "PHONE_NUMBER", label: "Phone Number" },
  { value: "ADDRESS", label: "Address" },
  { value: "EMERGENCY_CONTACT", label: "Emergency Contact" },
  { value: "MEDICAL_CONDITIONS_DISCLOSURE", label: "Medical Conditions Disclosure" },
] as const;

export type PlayerInfoFieldValue = (typeof PLAYER_INFO_FIELDS)[number]["value"];

/** Full wire shape of a league waiver — see WaiverResponse on the backend. */
export interface IWaiver {
  waiverId: string;
  leagueId: string;
  name: string;
  description: string;
  effectiveFromDate: string; // yyyy-MM-dd
  expiryDate: string | null; // yyyy-MM-dd
  agreementContent: string; // HTML
  playerInfoFields: string[];
  customFields: string[];
  requireAgreeCheckbox: boolean;
  agreeCheckboxLabel: string | null;
  requireDigitalSignature: boolean;
  requireDateConfirmation: boolean;
  requireParentGuardianIfMinor: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

/** Minimal shape needed to populate the tournament "Select Waiver Form" dropdown. */
export type IWaiverOption = Pick<IWaiver, "waiverId" | "name">;

export interface IWaiverRequest {
  name: string;
  description: string;
  effectiveFromDate: string;
  expiryDate?: string | null;
  agreementContent: string;
  playerInfoFields: string[];
  customFields: string[];
  requireAgreeCheckbox: boolean;
  agreeCheckboxLabel: string;
  requireDigitalSignature: boolean;
  requireDateConfirmation: boolean;
  requireParentGuardianIfMinor: boolean;
}

/** Unwraps the backend's { success, message, data, errorCode } envelope. */
function unwrapData<T>(res: { data?: { data?: T } }): T {
  return res?.data?.data as T;
}

/** GET league/{leagueId}/waiver — a league's waiver forms, most recently created first. Unauthenticated on the backend. */
export async function fetchLeagueWaivers(leagueId: string): Promise<IWaiver[]> {
  const res = await Api.get(`${QUERY_KEY.leagueWaivers}/${leagueId}/waiver`);
  return unwrapData<IWaiver[]>(res) ?? [];
}

/** GET league/{leagueId}/waiver/{waiverId} — a single waiver, for the edit form. */
export async function fetchWaiver(leagueId: string, waiverId: string): Promise<IWaiver> {
  const res = await Api.get(`${QUERY_KEY.leagueWaivers}/${leagueId}/waiver/${waiverId}`);
  return unwrapData<IWaiver>(res);
}

/** POST league/{leagueId}/waiver — LEAGUE_ADMIN only on the backend. */
export async function createWaiver(leagueId: string, request: IWaiverRequest): Promise<IWaiver> {
  const res = await Api.post(`${QUERY_KEY.leagueWaivers}/${leagueId}/waiver`, request);
  return unwrapData<IWaiver>(res);
}

/** PUT league/{leagueId}/waiver/{waiverId} — partial merge on the backend; we always send every field. */
export async function updateWaiver(
  leagueId: string,
  waiverId: string,
  request: IWaiverRequest
): Promise<IWaiver> {
  const res = await Api.put(`${QUERY_KEY.leagueWaivers}/${leagueId}/waiver/${waiverId}`, request);
  return unwrapData<IWaiver>(res);
}

/** DELETE league/{leagueId}/waiver/{waiverId} — 409 if a tournament still references it. */
export async function deleteWaiver(leagueId: string, waiverId: string): Promise<void> {
  await Api.delete(`${QUERY_KEY.leagueWaivers}/${leagueId}/waiver/${waiverId}`);
}
